import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  taxCategoriesCreate,
  taxCategoriesGetStripeTaxCodes,
} from "~/api-refs";
import {
  EMPTY_TAX_CATEGORY_FORM_VALUES,
  type TaxCategoryFormData,
  TaxCategoryFormModal,
  taxCategorySchema,
} from "~/components/settings/tax-category-form-modal";
import type { ComboboxItem } from "~/components/ui/combobox";
import { convexClient } from "~/integrations/convex/provider";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/settings/tax-categories/new"
)({
  component: CreateTaxCategoryModal,
});

function CreateTaxCategoryModal() {
  const navigate = useNavigate();

  const { data: stripeTaxCodes, isLoading: isLoadingTaxCodes } = useQuery({
    queryKey: ["stripeTaxCodes"],
    queryFn: () => convexClient.action(taxCategoriesGetStripeTaxCodes, {}),
    staleTime: 1000 * 60 * 60,
  });

  const taxCodeOptions: ComboboxItem<string>[] = (stripeTaxCodes ?? []).map(
    (code) => ({
      value: code.id,
      label: code.name,
      keywords: [code.id, code.description.toLowerCase()],
    })
  );

  const form = useForm<TaxCategoryFormData>({
    resolver: zodResolver(taxCategorySchema),
    defaultValues: EMPTY_TAX_CATEGORY_FORM_VALUES,
  });

  const createTaxCategoryMutation = useMutation({
    mutationFn: (data: { name: string; value: number; taxCode?: string }) => {
      return convexClient.mutation(taxCategoriesCreate, data);
    },
    onSuccess: (result) => {
      if (result) {
        ProductEvents.taxCategoryCreated(result);
      }
    },
  });

  const handleClose = () => {
    void navigate({ to: "/settings/tax-categories" });
  };

  const handleSubmit = async (values: TaxCategoryFormData) => {
    if (createTaxCategoryMutation.isPending) {
      return;
    }

    try {
      const taxValue =
        values.taxMethod === "automatic"
          ? 0
          : Number.parseFloat(values.value ?? "0");

      const taxCode =
        values.taxMethod === "automatic"
          ? (values.taxCode ?? undefined)
          : undefined;

      await createTaxCategoryMutation.mutateAsync({
        name: values.name,
        value: taxValue,
        taxCode,
      });
      toast.success("Tax Category added successfully");
      handleClose();
    } catch (error) {
      toast.error("Error adding Tax Category");
      logger.exception("Failed to create tax category", error);
    }
  };

  return (
    <TaxCategoryFormModal
      description="Create a new tax rate for your items."
      form={form}
      isLoadingTaxCodes={isLoadingTaxCodes}
      isSubmitting={createTaxCategoryMutation.isPending}
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitLabel="Add"
      taxCodeOptions={taxCodeOptions}
      title="Add Tax Category"
    />
  );
}
